import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { SAMPLE_ARTICLES } from '../constants';
import { ChevronRight, ChevronLeft, Calendar, User, ArrowLeft } from 'lucide-react';
import { formatDate, cn, getImageUrl } from '../lib/utils';
import { useTranslate } from '../context/LanguageContext';
import { useData } from '../context/DataContext';

export default function SecondaryHeroSlider() {
  const { language, t, translateArticles } = useTranslate();
  const { articles, heros } = useData();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  // Use custom heros if available in database, otherwise fall back to articles
  const hasHeros = heros && heros.length > 0;
  const rawArticles = articles && articles.length > 0 ? articles : SAMPLE_ARTICLES;
  
  const sliderItems = hasHeros
    ? heros.map((h: any) => ({
        id: h.id,
        title: language === 'ar' ? h.title_ar : (h.title_en || h.title_ar),
        summary: language === 'ar' ? h.subtitle_ar : (h.subtitle_en || h.subtitle_ar),
        imageUrl: h.imageUrl,
        linkUrl: h.linkUrl || '/',
        category: language === 'ar' ? 'مميز' : 'Featured',
        publishedDate: h.created_at || new Date().toISOString(),
        isCustomHero: true
      }))
    : translateArticles(rawArticles).filter(Boolean).slice(0, 10);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % sliderItems.length);
  }, [sliderItems.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + sliderItems.length) % sliderItems.length);
  }, [sliderItems.length]);

  useEffect(() => {
    if (isHovered) return;
    if (sliderItems.length <= 1) return;
    const interval = setInterval(nextSlide, 5000); // 5 seconds autoplay
    return () => clearInterval(interval);
  }, [nextSlide, isHovered, sliderItems.length]);

  const currentArticle = sliderItems[currentIndex];

  if (!currentArticle) return null;

  const linkHref = currentArticle.isCustomHero ? currentArticle.linkUrl : `/article/${currentArticle.id}`;

  return (
    <section 
      className="relative w-full h-[500px] bg-zinc-900 overflow-hidden my-8 md:my-[30px]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          className={cn("absolute inset-0 flex flex-col h-full", language === 'ar' ? 'md:flex-row' : 'md:flex-row-reverse')}
        >
          {/* RIGHT Side (in RTL): Image - Fixed size based on viewport */}
          <div className="w-full md:w-[600px] h-2/5 md:h-full relative overflow-hidden order-1 flex-shrink-0 group/img">
            {/* Main Image with Zoom Effect */}
            <motion.img 
              key={`img-${currentIndex}`}
              initial={{ scale: 1.2, filter: 'grayscale(100%)' }}
              animate={{ scale: 1.05, filter: 'grayscale(0%)' }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              src={getImageUrl(currentArticle.imageUrl)} 
              alt={currentArticle.title}
              className="w-full h-full object-cover"
            />
            
            {/* Visual Effects & Borders */}
            <div className="absolute inset-0 border-[6px] md:border-[12px] border-black/40 z-10 pointer-events-none" />
            <div className="absolute inset-[6px] md:inset-[12px] border border-white/10 z-10 pointer-events-none" />
            
            {/* Glowing Corners */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-secondary/30 blur-[40px] z-0 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-zinc-900/80 blur-[20px] z-0 pointer-events-none" />
            
            {/* Overlays */}
            <div className={cn("absolute inset-0 via-transparent to-transparent md:block hidden z-10", language === 'ar' ? "bg-gradient-to-l from-zinc-950/80" : "bg-gradient-to-r from-zinc-950/80")} />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-zinc-950/80 to-transparent md:hidden block z-10" />
            <div className="absolute inset-0 bg-zinc-900/20 group-hover/img:bg-transparent transition-colors duration-700 z-10" />
            
            {/* Shiny Scan Effect */}
            <motion.div 
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear", repeatDelay: 2 }}
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 z-20 pointer-events-none"
            />
          </div>

          {/* LEFT Side (in RTL): Content - Grow to fill */}
          <div className={cn("w-full flex-1 h-3/5 md:h-full flex items-center relative z-20 bg-zinc-950/40 backdrop-blur-sm px-6 md:px-12 lg:px-20 order-2", language === 'ar' ? "text-right" : "text-left")}>
            <div className="max-w-2xl py-6 md:py-0 w-full">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <div className={cn("flex items-center gap-3 mb-6", language === 'ar' ? "justify-start" : "justify-start")}>
                  <span className="px-3 py-1 bg-secondary text-zinc-950 text-[10px] font-black uppercase tracking-wider rounded-md">
                    {currentArticle.category}
                  </span>
                  <div className="flex items-center gap-1.5 text-white/50 text-[11px] font-bold font-sans">
                    <Calendar size={12} />
                    {formatDate(currentArticle.publishedDate)}
                  </div>
                </div>

                <h2 className="text-xl md:text-3xl lg:text-4xl font-black text-white mb-5 leading-snug group-hover:text-secondary transition-colors line-clamp-3">
                  <a href={linkHref} className="hover:text-secondary transition-colors">{currentArticle.title}</a>
                </h2>

                <p className="text-[13px] md:text-base text-white/70 mb-8 leading-relaxed font-normal not-italic line-clamp-4 hidden md:block">
                  {currentArticle.summary}
                </p>

                <div className={cn("flex items-center", language === 'ar' ? "justify-start" : "justify-start")}>
                  <a 
                    href={linkHref}
                    className="flex items-center gap-2 px-6 py-3 bg-white text-primary hover:bg-secondary hover:text-white transition-all text-xs font-black uppercase tracking-widest rounded-full group"
                  >
                    <span>{t('اقرأ المزيد')}</span> 
                    <ArrowLeft size={16} className={cn("transition-transform duration-300", language === 'ar' ? "group-hover:-translate-x-1" : "group-hover:translate-x-1 rotate-180")} />
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows - Smaller and repositioned */}
      <div className="absolute bottom-6 left-6 lg:left-16 flex items-center gap-2 z-30">
        <button 
          onClick={prevSlide}
          className="w-8 h-8 rounded-full border border-white/10 bg-black/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-secondary hover:border-secondary transition-all"
        >
          <ChevronRight size={18} />
        </button>
        <button 
          onClick={nextSlide}
          className="w-8 h-8 rounded-full border border-white/10 bg-black/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-secondary hover:border-secondary transition-all"
        >
          <ChevronLeft size={18} />
        </button>
      </div>

      {/* Progress Dots - Repositioned */}
      <div className="absolute bottom-6 right-6 lg:right-16 flex gap-2 z-30">
        {sliderItems.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={cn(
              "h-1 transition-all duration-500 rounded-full",
              currentIndex === index ? "w-8 bg-secondary" : "w-1 bg-white/20 hover:bg-white/40"
            )}
          />
        ))}
      </div>
      {/* Visual Accents */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-secondary/5 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/10 blur-[120px] rounded-full pointer-events-none" />
    </section>
  );
}
